import React, { useContext, useEffect, useState } from "react";
import {
   Alert as Alert2, Button, ButtonGroup, Card, CardBody, Col, FormFeedback, FormGroup,
   Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table
} from "reactstrap";
import { DebounceInput } from "react-debounce-input";
import { toast } from "react-toastify";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, filterHelpers, helpers, UserContext } from "../utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import {
   ConfirmModal,
   CustomerTypeahead,
   FilterableScreen,
   FilterDateTime,
   FilterSelect,
   FilterSwitch,
   MachinePartTypeahead,
   Pager,
   NotificationSettings,
   ServiceThreadSpinner
} from "./";
import NumberFormat from "react-number-format";
import _ from "lodash";

const SAVE_FAILED_MESSAGE = "The machine work order could not be saved. If this persists, contact a system admin.";
const COMPLETE_FAILED_MESSAGE = "The machine work order could not be completed. If this persists, contact a system admin.";
const PRICING_LOAD_FAILED_MESSAGE = "This customer's machine part prices could not be loaded. If this persists, contact a system admin.";
const STANDARD_PARTS_LOAD_FAILED_MESSAGE = "The standard work order parts could not be loaded. If this persists, contact a system admin.";
// Two effects load the parts and prices on one customer change, so a failure would otherwise toast twice.
const PRICING_LOAD_TOAST_ID = "machine-wo-pricing-load";
const STANDARD_PARTS_LOAD_TOAST_ID = "machine-wo-standard-parts-load";

const emptyNew = {
   billToAddress: {},
   btAddressCode: "",
   customerId: "",
   inventoryLocation: constants.MACHINE_WORKORDER_SITES[0], //defaults to first site
   machineWorkOrderId: -1,
   shipToAddress: {},
   workOrderItems: [],
};

const emptyItem = {
   itemNumber: null,
   itemDescription: "",
   itemPrice: "",
   quantity: 1,
};

const emptyAddress = {
   addressCode: "",
   contact: "",
   address1: "",
   address2: "",
   address3: "",
   country: null,
   city: "",
   state: "",
   zip: "",
   phone: "",
   fax: "",
   countryCode: "US",
};

//const DEFAULT_BILL_TO_CODE = "MAIN";

function CurrencyFormatInput(props) {
   function onChange(e) {
      let value = e.target.value;
      value = helpers.formatCurrency(value);
      value = _.replace(value, "$", "");
      props.onChange(parseFloat(value).toFixed(2));
   }
   return (
      <div className="currencyInput">
         <DebounceInput
            value={props.value && parseFloat(props.value).toFixed(2)}
            onChange={onChange}
            className="form-control"
            debounceTimeout={1500}
         />
         <i>$</i>
      </div>
   );
}

function getCityStateZip(item, field) {
   let address = item[field];
   if (address && address.city) {
      return `${address.city}, ${address.state} ${address.zip}`;
   }
   return "";
}

function AddEditItem(props) {
   const {
      editItem,
      setEditItem,
      modalClassName,
      toggleEditRow,
      addressOptions,
      salespeopleList,
      saving,
      priceList,
      onSave,
      onSavePrint,
      setConfirmCompleteWO,
      downloadWorkOrder,
      fetchCustomerAddresses,
   } = props;
   const [addressSaving, setAddressSaving] = useState(false);
   const [editCopy, setEditCopy] = useState(null);
   const [editIndex, setEditIndex] = useState(null);
   const [emailInvalid, setEmailInvalid] = useState(false);
   const [emailList, setEmailList] = useState(editItem.invoiceRecipients || []);
   const [flags, setFlags] = useState(editItem.flags);
   const [modalWarnings, setModalWarnings] = useState(null);
   const [newAddress, setNewAddress] = useState(_.assign({}, emptyAddress));
   const [newEmail, setNewEmail] = useState();
   const [rowCopy, setRowCopy] = useState(null);
   const [rowItemDelete, setRowItemDelete] = useState(null);
   const [savingDelete, setSavingDelete] = useState(false);
   const [selectedEmail, setSelectedEmail] = useState(null);
   const [showNewAddress, setShowNewAddress] = useState(false);
   const isComplete = editItem.workOrderComplete;
   const availableEmails = _.chain(addressOptions)
      .map((x) => x.emailList)
      .flatten()
      .uniq()
      .reject((x) => _.some(emailList, (y) => y === x) || x === "")
      .map((x) => {
         return {
            value: x,
            label: x,
         };
      })
      .value();

   useEffect(() => {
      if (!flags && editItem.flags) {
         setFlags(editItem.flags);
      }
      if (!_.isEqual(emailList, editItem.invoiceRecipients)) {
         onChange("invoiceRecipients", emailList);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [editItem, flags, emailList]);

   useEffect(() => {
      let editRowItem = _.find(editItem.workOrderItems, (x) => {
         return x.editing;
      });
      if (!rowCopy) {
         setRowCopy(editRowItem);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [editItem.workOrderItems]);

   function addNewPart() {
      let newList = Object.assign([], editItem?.workOrderItems);
      newList = _.map(newList, (x, index) => {
         x.editing = false;
         return x;
      });
      let changes = _.assign({}, editItem);
      let newItem = _.assign({}, emptyItem);
      newItem.customerId = changes.customerId;
      newItem.editing = true;
      newList.push(newItem);
      changes.workOrderItems = newList;
      setEditItem(changes);
   }

   function addressArea(type) {
      let address = editItem && editItem[type];
      let isShipTo = type === "shipToAddress";

      return (
         <Col>
            <Row className="my-2">
               <Col>
                  <h6>
                     <strong>{_.startCase(type)}</strong>
                  </h6>
               </Col>
               {isShipTo && (
                  <Col>
                     <ButtonGroup className="float-end btn-grp-sm">
                        <Button
                           className="icon-button btn-sm"
                           color="success"
                           onClick={() => setShowNewAddress(true)}
                           title="Add new shipping address"
                           disabled={isComplete}
                        >
                           <FontAwesomeIcon className="whiteIcon" icon="plus" />
                        </Button>
                     </ButtonGroup>
                  </Col>
               )}
            </Row>
            <Row className="mb-2">
               <Col xs="3">
                  <h6>
                     Address Id
                     {/* { isShipTo  && <> {helpers.requiredStar()}</>} */}
                     {isShipTo && !address ? helpers.requiredStar() : null}
                  </h6>
               </Col>
               <Col xs="9">
                  <Select
                     classNamePrefix="serviceThreadSelect"
                     closeMenuOnSelect={true}
                     components={makeAnimated()}
                     options={addressOptions}
                     onChange={(selection) => onChange(type, selection)}
                     value={editItem[type]}
                     isDisabled={isComplete}
                  />
               </Col>
            </Row>
            {isShipTo ? (
               <>
                  <Row className="mb-2">
                     <Col xs="3">
                        <h6>Contact Name</h6>
                     </Col>
                     <Col xs="9">
                        <Input defaultValue={address?.contact} readOnly />
                     </Col>
                  </Row>
                  <Row className="mb-2">
                     <Col xs="3">
                        <h6>
                           Company {isShipTo && !address ? helpers.requiredStar() : null}
                        </h6>
                     </Col>
                     <Col xs="9">
                        <Input defaultValue={address?.address1} readOnly />
                     </Col>
                  </Row>
               </>
            ) : null}
            <Row className="mb-2">
               <Col xs="3">
                  <h6>
                     Address 1{isShipTo && !address ? helpers.requiredStar() : null}
                  </h6>
               </Col>
               <Col xs="9">
                  <Input defaultValue={address?.address2} readOnly />
               </Col>
            </Row>
            <Row className="mb-2">
               <Col xs="3">
                  <h6>Address 2</h6>
               </Col>
               <Col xs="9">
                  <Input defaultValue={address?.address3} readOnly />
               </Col>
            </Row>
            {isShipTo ? (
               <Row className="mb-2">
                  <Col xs="3">
                     <h6>Country</h6>
                  </Col>
                  <Col xs="9">
                     <Input defaultValue={address?.stCountryName || "United States"} readOnly />
                  </Col>
               </Row>
            ) : null}
            <Row className="mb-2">
               <Col xs="3">
                  <h6>
                     City, State, Zip
                     {isShipTo && !address ? helpers.requiredStar() : null}
                  </h6>
               </Col>
               <Col xs="9">
                  <Input defaultValue={getCityStateZip(editItem, type)} readOnly />
               </Col>
            </Row>
            {isShipTo ? (
               <>
                  <Row className="mb-2">
                     <Col xs="3">
                        <h6>Phone</h6>
                     </Col>
                     <Col xs="9">
                        <Input defaultValue={address?.formattedPhone} readOnly />
                     </Col>
                  </Row>
                  <Row>
                     <Col xs="3">
                        <h6>Fax</h6>
                     </Col>
                     <Col xs="9">
                        <Input defaultValue={address?.formattedFax} readOnly />
                     </Col>
                  </Row>
               </>
            ) : (
               <>
                  <Row className="mb-2">
                     <Col xs="3">
                        <h6>Salesperson</h6>
                     </Col>
                     <Col xs="9">
                        <Select
                           classNamePrefix="serviceThreadSelect"
                           closeMenuOnSelect={true}
                           components={makeAnimated()}
                           options={salespeopleList}
                           onChange={(selection) => onChange("salesPerson", selection)}
                           value={editItem.salesPerson}
                           isDisabled={isComplete}
                        />
                     </Col>
                  </Row>
               </>
            )}
         </Col>
      );
   }

   function addressPayload() {
      let warnings = [];

      if (!newAddress.city) {
         warnings.push("City is required.");
      }
      if (!newAddress.state) {
         warnings.push("State is required.");
      }
      if (!newAddress.zip) {
         warnings.push("Zip code is required.");
      }
      if (!newAddress.address1) {
         warnings.push("Company name is required.");
      }
      if (!newAddress.address2) {
         warnings.push("Address line 1 is required");
      }
      if (warnings.length) {
         setModalWarnings({
            flavor: "alert-danger",
            text: warnings.join(" "),
         });
         return;
      } else {
         const payload = {
            AccountId: editItem.customerId,
            AddressCode: "-- New Address --", // placeholder
            Address1: newAddress.address1,
            Address2: newAddress.address2,
            Address3: newAddress.address3,
            City: newAddress.city,
            State: newAddress.state,
            Zip: newAddress.zip,
            CountryCode: newAddress.countryCode,
            Country: newAddress.country,
            Contact: newAddress.contact,
            Phone: newAddress.phone,
            Fax: newAddress.fax,
         };
         return payload;
      }
   }

   function allowEmailEdit(index) {
      setEditIndex(index);
      setEditCopy(emailList[index]);
   }

   function calculateOrderTotal() {
      let total = 0;
      if (editItem && editItem.workOrderItems) {
         _.forEach(editItem.workOrderItems, (item) => {
            if (item.extendedPrice) total += item.extendedPrice;
         });
      }
      return total;
   }

   function cancelEmailEdit() {
      setEditIndex(null);
      setEditCopy(null);
   }

   function closeNewAddress() {
      setNewAddress(_.assign({}, emptyAddress));
      setShowNewAddress(false);
   }

   function deleteWorkOrderItem(id) {
      let newList = Object.assign([], editItem?.workOrderItems);
      newList = _.reject(newList, { machineWorkOrderDetailId: id });
      let changes = Object.assign({}, editItem);
      changes.workOrderItems = newList;
      setEditItem(changes);
   }

   function onChange(field, value) {
      let changes = Object.assign({}, editItem);
      changes[field] = value;
      setEditItem(changes);
   }

   function onChangeNewAddress(field, value) {
      let changes = Object.assign({}, newAddress);
      changes[field] = value;
      setNewAddress(changes);
   }

   function onRowItemChange(field, value, index) {
      console.log("field", field);
      console.log("value", value);
      console.log("index", index);
      if (!value) return;
      let editRowItem = _.assign({}, _.find(editItem.workOrderItems, (x) => { return x.editing; }));
      if (field === "itemNumber" && value) {
         editRowItem.itemDescription = value.itemDescription;
         value = value.itemNumber;
         const itemPricing = _.find(priceList, (x) => {
            return x.itemNumber === value;
         });
         editRowItem = _.merge(editRowItem, itemPricing);
      }
      editRowItem[field] = value;
      //updating extendedPrice for any changes
      console.log("editRowItem", editRowItem);
      let correctPrice = editRowItem.itemPrice || editRowItem.listPrice;
      editRowItem.extendedPrice = editRowItem.quantity * correctPrice;
      let changes = _.assign({}, editItem);
      let newList = _.assign([], changes.workOrderItems);
      newList[index] = editRowItem;
      changes.workOrderItems = newList;
      setEditItem(changes);
   }

   function onWorkOrderItemDelete(id, itemNumber) {
      if (!savingDelete) {
         setSavingDelete(true);
         api
            .fetch(`SewingTech/DeleteMachineWorkOrderDetail`, {
               MachineWorkOrderDetailId: id,
            })
            .then((res) => {
               if (res && res.data && res.data.success) {
                  deleteWorkOrderItem(id);
                  setRowItemDelete();
               }
            })
            .catch(api.catchHandler)
            .finally(() => setSavingDelete(false));
      }
   }

   function removeEmail(index) {
      let newList = Object.assign([], emailList);
      newList = newList.splice(0, index);
      setEmailList(newList);
   }

   function saveEditItemRow(idx) {
      let newList = Object.assign([], editItem?.workOrderItems);
      newList = _.map(newList, (x, index) => {
         if (idx === index && !x.itemPrice) {
            x.editing = false;
            x.itemPrice = x.listPrice;
            // no longer need row copy
            setRowCopy(null);
         } else {
            x.editing = false;
            // no longer need row copy
            setRowCopy(null);
         }
         return x;
      });
      setEditItem({
         ...editItem,
         workOrderItems: newList,
      });
   }

   function saveNewAddress() {
      setModalWarnings(null);
      let payload = addressPayload();
      if (payload) {
         setAddressSaving(true);
         api
            .post("Customer/CreateNewAddress", payload)
            .then((res) => {
               if (res && res.data) {
                  fetchCustomerAddresses();
                  setNewAddress(_.assign({}, emptyAddress));
                  setShowNewAddress(false);
               }
            })
            .catch(api.catchHandler)
            .finally(() => setAddressSaving(false));
      }
   }

   function toggleItemEditRow(idx, resetOverride) {
      let newList = Object.assign([], editItem?.workOrderItems);
      newList = _.map(newList, (x, index) => {
         x.editing = resetOverride || idx === index ? !x.editing : false;

         if (!x.editing && rowCopy && idx === index) {
            // if cancelling without saving, reverting to original copy
            x = rowCopy;
            setRowCopy(null);
         }
         return x;
      });
      let changes = Object.assign({}, editItem);
      changes.workOrderItems = newList;
      setEditItem(changes);
   }

   function tryAddEmail(updatedEmail) {
      if (helpers.emailIsValid(updatedEmail)) {
         setEmailInvalid(false);
         const emailListCopy = _.slice(emailList);
         if (
            _.findIndex(emailListCopy, (currentEmail) => {
               return currentEmail === updatedEmail;
            }) !== -1
         ) {
            return;
         }

         if (editIndex === null) {
            emailListCopy.push(newEmail || selectedEmail);
            setNewEmail("");
            setSelectedEmail("");
         } else {
            emailListCopy[editIndex] = updatedEmail;
            setEditIndex(null);
         }
         setEmailList(emailListCopy);
         setSelectedEmail("");
      } else {
         setEmailInvalid(true);
      }
   }

   function verifyRowItemDelete(item, index) {
      if (item.machineWorkOrderDetailId > 0) {
         setRowItemDelete({
            itemNumber: item.itemNumber,
            id: item.machineWorkOrderDetailId,
            index: index,
         });
      } else {
         deleteWorkOrderItem(item.machineWorkOrderDetailId);
      }
   }

   return (
      <>
         <Card>
            {console.log("editItem", editItem)}
            {console.log("deleteitem", rowItemDelete)}
            <CardBody>
               <Row>
                  <Col>
                     <h5 className={modalClassName === "darkTheme" ? "" : "text-blue"}>
                        Work Order #{" "}
                        {editItem.machineWorkOrderId < 1
                           ? "TBD"
                           : editItem.machineWorkOrderId}
                     </h5>
                  </Col>
                  <Col>
                     <h5 className={modalClassName === "darkTheme" ? "" : "text-blue"}>
                        Customer # {editItem.customerId}
                     </h5>
                  </Col>
                  <Col>
                     <Button
                        onClick={() => {
                           toggleEditRow(null, false);
                           closeNewAddress();
                        }}
                        className="float-end btn-sm"
                     >
                        Back to List
                     </Button>
                  </Col>
               </Row>
               <Row>
                  {addressArea("billToAddress")}
                  {addressArea("shipToAddress")}
               </Row>
            </CardBody>
         </Card>
         <Card className="mt-2">
            <CardBody>
               <Row>
                  <Col xs="6">
                     <Row className="mb-2">
                        <Col xs="3">
                           <h6>PO Number</h6>
                        </Col>
                        <Col xs="9">
                           <Input
                              value={editItem?.poNumber}
                              onChange={(e) => onChange("poNumber", e.target.value)}
                              disabled={isComplete}
                           />
                        </Col>
                     </Row>
                  </Col>
                  <Col xs="6">
                     <Row className="mb-2">
                        <Col xs="3">
                           <h6>Machine Model#</h6>
                        </Col>
                        <Col xs="9">
                           <Input
                              value={editItem?.machineModel}
                              onChange={(e) => onChange("machineModel", e.target.value)}
                              disabled={isComplete}
                           />
                        </Col>
                     </Row>
                  </Col>
                  <Col xs="6">
                     <Row className="mb-2">
                        <Col xs="3">
                           <h6>Tracking #</h6>
                        </Col>
                        <Col xs="9">
                           <Input
                              value={editItem?.trackingNumber}
                              onChange={(e) => onChange("trackingNumber", e.target.value)}
                              disabled={isComplete}
                           />
                        </Col>
                     </Row>
                  </Col>
                  <Col xs="6">
                     <Row className="mb-2">
                        <Col xs="3">
                           <h6>Serial #</h6>
                        </Col>
                        <Col xs="9">
                           <Input
                              value={editItem?.machineSerial}
                              onChange={(e) => onChange("machineSerial", e.target.value)}
                              disabled={isComplete}
                           />
                        </Col>
                     </Row>
                  </Col>
                  <Col xs="6">
                     <Row className="mb-0">
                        <Col xs="3">
                           <h6>Shipping Cost</h6>
                        </Col>
                        <Col xs="9">
                           <CurrencyFormatInput
                              value={editItem?.shippingCost}
                              onChange={(value) => onChange("shippingCost", value)}
                              disabled={isComplete}
                           />
                        </Col>
                     </Row>
                     <Row className="mb-4 fontSize85">
                        <Col xs="12">
                           <i>Zero (0.00) means a no-charge shipment</i>
                        </Col>
                     </Row>
                  </Col>
                  <Col xs="6">
                     <Row className="mb-0">
                        <Col xs="3">
                           <h6>Freight Cost</h6>
                        </Col>
                        <Col xs="9">
                           <CurrencyFormatInput
                              value={editItem?.freightQuote}
                              onChange={(value) => onChange("freightQuote", value)}
                              disabled={isComplete}
                           />
                        </Col>
                     </Row>
                     <Row className="mb-4 fontSize85">
                        <Col xs="12">
                           <i>For freight bill auditing</i>
                        </Col>
                     </Row>
                  </Col>
                  <Col xs="6">
                     <Row className="mb-2">
                        <Col xs="3">
                           <h6>Labor</h6>
                        </Col>
                        <Col xs="3">
                           <h6>
                              Hours
                              {editItem?.laborHours === "" ? helpers.requiredStar() : ""}
                           </h6>
                        </Col>
                        <Col xs="6">
                           <Input
                              value={editItem?.laborHours}
                              onChange={(e) => onChange("laborHours", e.target.value)}
                              onBlur={(e) => onChange("laborHours", helpers.roundToNearestFourth(e.target.value))}
                              disabled={isComplete}
                           />
                        </Col>
                     </Row>
                  </Col>
                  <Col xs="6">
                     <Row className="mb-0">
                        <Col xs="3">
                           <h6>Rate</h6>
                        </Col>
                        <Col xs="9">
                           <CurrencyFormatInput
                              value={editItem?.laborRate}
                              onChange={(value) => onChange("laborRate", value)}
                              disabled={isComplete}
                           />
                        </Col>
                     </Row>
                  </Col>
                  <Col xs="6">
                     <Row className="mb-2">
                        <Col xs="3">
                           <h6>Notes</h6>
                        </Col>
                        <Col xs="9">
                           <Input
                              className="serviceThreadTextArea"
                              value={editItem?.notes}
                              onChange={(e) => onChange("notes", e.target.value)}
                              type="textarea"
                           />
                        </Col>
                     </Row>
                  </Col>
                  <Col xs="6">
                     <Row className="mb-0">
                        <Col xs="3">
                           <h6>Inventory Location</h6>
                        </Col>
                        <Col xs="9">
                           <Select
                              classNamePrefix="serviceThreadSelect"
                              closeMenuOnSelect={true}
                              components={makeAnimated()}
                              options={constants.MACHINE_WORKORDER_SITES}
                              onChange={(selection) => onChange("inventoryLocation", selection)}
                              value={editItem.inventoryLocation}
                              isDisabled={isComplete}
                           />
                        </Col>
                     </Row>
                  </Col>
               </Row>
            </CardBody>
         </Card>
         <Card className="mt-2">
            <CardBody>
               <Row>
                  <Col>
                     {isComplete ? (
                        <h5 className="text-danger mb-0">No Items Added.</h5>
                     ) : (
                        <Table striped hover size="sm" responsive={false} id="userTable">
                           <thead className="fontSize85">
                              <tr>
                                 <th width="22%">Item Number</th>
                                 <th width="33%">Description</th>
                                 <th width="8%">Quantity</th>
                                 <th width="8%">Price</th>
                                 <th width="8%">
                                    Extended
                                    <br />
                                    Price
                                 </th>
                                 <th />
                              </tr>
                           </thead>
                           <tbody>
                              {editItem &&
                                 editItem.workOrderItems &&
                                 editItem.workOrderItems.length > 0
                                 ? _.map(editItem.workOrderItems, (t, index) => (
                                    <tr
                                       key={`item-${editItem.machineWorkOrderId}-${t.itemNumber}${index}`}
                                       className="fontSize85"
                                    >
                                       <td>
                                          {t.editing && !isComplete ? (
                                             <MachinePartTypeahead
                                                onChange={(e) => onRowItemChange("itemNumber", e, index)}
                                                value={{
                                                   value: t.itemNumber,
                                                   label: t.itemNumber,
                                                }}
                                                placeholder="Select an EXISTING machine part item number"
                                             />
                                          ) : (
                                             t.itemNumber
                                          )}
                                       </td>
                                       <td>{t.itemDescription}</td>
                                       <td>
                                          {t.editing && !isComplete ? (
                                             <NumberFormat
                                                className="form-control height38"
                                                thousandSeparator={true}
                                                decimalScale={0}
                                                allowNegative={false}
                                                onValueChange={(e) => {
                                                   let value = e.value;
                                                   onRowItemChange("quantity", value, index);
                                                }}
                                                value={t.quantity}
                                             />
                                          ) : (
                                             t.quantity
                                          )}
                                       </td>
                                       <td>
                                          {t.editing && !isComplete ? (
                                             <NumberFormat
                                                className="form-control height38"
                                                style={{ width: "130px" }}
                                                prefix="$"
                                                thousandSeparator={true}
                                                decimalSeparator="."
                                                decimalScale={2}
                                                fixedDecimalScale={true}
                                                allowNegative={false}
                                                onValueChange={(e) => {
                                                   let value = e.value;
                                                   onRowItemChange("itemPrice", value, index);
                                                }}
                                                value={ t.itemPrice ? t.itemPrice : t.listPrice }
                                             />
                                          ) : (
                                             helpers.formatCurrency(t.itemPrice)
                                          )}
                                       </td>
                                       <td>{helpers.formatCurrency(t.extendedPrice)}</td>
                                       <td>
                                          {isComplete ? null : (
                                             <ButtonGroup className="btn-group-sm btn-sm float-end">
                                                <Button
                                                   className={`icon-button me-1 btn-sm`}
                                                   color={t.editing ? "success" : "info"}
                                                   title={t.editing ? "Save changes" : "Edit item"}
                                                   onClick={() => {
                                                      t.editing
                                                         ? saveEditItemRow(index)
                                                         : toggleItemEditRow(index);
                                                   }}
                                                   disabled={saving}
                                                >
                                                   <FontAwesomeIcon className="whiteIcon" icon={t.editing ? "check" : "edit"} />
                                                </Button>
                                                <Button
                                                   color={t.editing ? "secondary" : "danger"}
                                                   className="icon-button btn-sm me-1"
                                                   title={t.editing ? "Cancel" : "Delete"}
                                                   onClick={() => {
                                                      t.editing
                                                         ? toggleItemEditRow(index)
                                                         : verifyRowItemDelete(t, index);
                                                   }}
                                                   disabled={saving}
                                                >
                                                   <FontAwesomeIcon
                                                      className="whiteIcon"
                                                      icon={t.editing ? "times" : "trash"}
                                                   />
                                                </Button>
                                             </ButtonGroup>
                                          )}
                                       </td>
                                    </tr>
                                 ))
                                 : null}
                           </tbody>
                           <tfoot className="table-footer">
                              <tr>
                                 <td colSpan={"4"} />
                                 <td>
                                    {calculateOrderTotal()
                                       ? helpers.formatCurrency(calculateOrderTotal())
                                       : helpers.formatCurrency(editItem?.orderTotal)}
                                 </td>
                                 <td colSpan={"1"}>
                                    <Button
                                       className={`me-1 btn-sm float-end`}
                                       onClick={() => addNewPart()}
                                       disabled={saving}
                                    >
                                       <FontAwesomeIcon className="whiteIcon" icon={"plus"} />
                                       <span className={"ps-1"}>New Part</span>
                                    </Button>
                                 </td>
                              </tr>
                           </tfoot>
                        </Table>
                     )}
                  </Col>
               </Row>
            </CardBody>
         </Card>
         <Card className="mt-2">
            <CardBody>
               <Row className="mb-3">
                  <Col>
                     <span className="h5">Invoice Email Settings</span>
                     <Row className="mt-3 ps-5">
                        Orders will be sent via the following method(s)
                     </Row>
                     <div className="ps-5">
                        <NotificationSettings
                           flags={flags}
                           setFlags={setFlags}
                           type="Invoice"
                        />
                     </div>
                  </Col>
               </Row>
               <Row>
                  <Col>
                     <Row>
                        <Col>
                           <FormGroup row>
                              <Label children="Contact Emails:" xs={2} className="fw-bold" />
                              <Col xs={8}>
                                 <Input
                                    id="newEmail"
                                    value={newEmail || ""}
                                    onChange={(e) => {
                                       setNewEmail(e.target.value);
                                       if (!e.target.value) setEmailInvalid(false);
                                    }}
                                    invalid={emailInvalid && newEmail}
                                    disabled={isComplete}
                                 />
                                 {emailInvalid && newEmail && (
                                    <FormFeedback children="Invalid email. Please correct and try again." />
                                 )}
                              </Col>
                              <Col xs={2}>
                                 <Button
                                    className="shredButton w-100"
                                    children="Add Email"
                                    onClick={() => tryAddEmail(newEmail)}
                                    disabled={isComplete}
                                 />
                              </Col>
                           </FormGroup>
                        </Col>
                     </Row>
                     <Row className="mb-2">
                        <Label children="Available Emails:" xs={2} className="fw-bold" />
                        <Col xs={8}>
                           <Select
                              options={availableEmails}
                              onChange={(selection) => setSelectedEmail(selection.value)}
                              value={
                                 selectedEmail
                                    ? _.find(availableEmails, (x) => {
                                       return x.value === selectedEmail;
                                    })
                                    : null
                              }
                              isDisabled={isComplete}
                           />
                        </Col>
                        <Col xs={2}>
                           <Button
                              className="shredButton w-100"
                              children="Add Email"
                              onClick={() => tryAddEmail(selectedEmail)}
                              disabled={isComplete}
                           />
                        </Col>
                     </Row>
                     <Row className="mt-3 ps-5">
                        Orders will be sent to the following email(s):
                     </Row>
                     <Row>
                        <Col>
                           {_.map(emailList, (email, index) => {
                              return (
                                 <Row key={index}>
                                    <Col xs={{ size: 8, offset: 2 }}>
                                       <FormGroup>
                                          <Input
                                             value={editIndex === index ? editCopy : email}
                                             onChange={(e) => setEditCopy(e.target.value)}
                                             invalid={emailInvalid && editIndex === index}
                                             className={editIndex === index ? "" : "readonly-input"}
                                             readOnly={editIndex !== index || isComplete}
                                          />
                                          {emailInvalid && editIndex === index && (
                                             <FormFeedback
                                                visible={emailInvalid}
                                                children="Invalid email. Please correct and try again."
                                             />
                                          )}
                                       </FormGroup>
                                    </Col>
                                    <Col className="text-center" xs={2}>
                                       <ButtonGroup>
                                          <Button
                                             className="icon-button me-1 btn-sm"
                                             color={editIndex === index ? "success" : "info"}
                                             onClick={
                                                editIndex === index
                                                   ? () => tryAddEmail(editCopy)
                                                   : () => allowEmailEdit(index)
                                             }
                                             disabled={
                                                (editIndex !== null && editIndex !== index) ||
                                                isComplete
                                             }
                                          >
                                             <FontAwesomeIcon className="whiteIcon" icon={editIndex === index ? "check" : "edit"} />
                                          </Button>
                                          <Button
                                             className="icon-button btn-sm"
                                             color={editIndex === index ? "secondary" : "danger"}
                                             onClick={
                                                editIndex === index
                                                   ? () => cancelEmailEdit()
                                                   : () => removeEmail(index)
                                             }
                                             title={editIndex === index ? "Discard Changes" : "Delete Email"}
                                             disabled={
                                                (editIndex !== null && editIndex !== index) ||
                                                isComplete
                                             }
                                          >
                                             <FontAwesomeIcon className="whiteIcon" icon={editIndex === index ? "times" : "trash"} />
                                          </Button>
                                       </ButtonGroup>
                                    </Col>
                                 </Row>
                              );
                           })}
                        </Col>
                     </Row>
                  </Col>
               </Row>
               {isComplete ? (
                  <Row>
                     <Col>
                        <Button className="sz202 float-start" onClick={() => downloadWorkOrder(editItem.machineWorkOrderId)}>
                           Print Work Order
                        </Button>
                     </Col>
                  </Row>
               ) : (
                  <Row>
                     <Col>
                        <Button className="sz202" disabled={saving} onClick={onSave}>
                           Save Work Order
                        </Button>
                     </Col>
                     <Col>
                        <Button className="sz202" disabled={saving} onClick={onSavePrint}>
                           Save &amp; Print Work Order
                        </Button>
                     </Col>
                     <Col>
                        <Button className="sz202" disabled={saving} onClick={() => setConfirmCompleteWO(true)}>
                           Complete Work Order
                        </Button>
                     </Col>
                  </Row>
               )}
            </CardBody>
         </Card>

         <Modal id="newAddressArea" isOpen={showNewAddress} contentClassName={modalClassName}>
            <ModalHeader children="New Address" />
            <ModalBody>
               {modalWarnings ? (
                  <Alert2 className={modalWarnings.flavor}>
                     {modalWarnings.text}
                  </Alert2>
               ) : null}
               <Row className="mb-2">
                  <Col xs="3">
                     <h6>Contact Name</h6>
                  </Col>
                  <Col xs="9">
                     <DebounceInput
                        value={newAddress.contact}
                        onChange={(e) => onChangeNewAddress("contact", e.target.value)}
                        debounceTimeout={500}
                     />
                  </Col>
               </Row>
               <Row className="mb-2">
                  <Col xs="3">
                     <h6>
                        Company{" "}
                        {!newAddress.address1?.length ? helpers.requiredStar() : null}
                     </h6>
                  </Col>
                  <Col xs="9">
                     <DebounceInput
                        value={newAddress.address1}
                        onChange={(e) => onChangeNewAddress("address1", e.target.value)}
                        debounceTimeout={500}
                     />
                  </Col>
               </Row>
               <Row className="mb-2">
                  <Col xs="3">
                     <h6>
                        Address 1{" "}
                        {!newAddress.address2?.length ? helpers.requiredStar() : null}
                     </h6>
                  </Col>
                  <Col xs="9">
                     <DebounceInput
                        value={newAddress.address2}
                        onChange={(e) => onChangeNewAddress("address2", e.target.value)}
                        debounceTimeout={500}
                     />
                  </Col>
               </Row>
               <Row className="mb-2">
                  <Col xs="3">
                     <h6>Address 2</h6>
                  </Col>
                  <Col xs="9">
                     <DebounceInput
                        value={newAddress.address3}
                        onChange={(e) => onChangeNewAddress("address3", e.target.value)}
                        debounceTimeout={500}
                     />
                  </Col>
               </Row>
               <Row className="mb-2">
                  <Col xs="3">
                     <h6>Country</h6>
                  </Col>
                  <Col xs="9">
                     <Input defaultValue={newAddress?.stCountryName || "United States"} readOnly />
                  </Col>
               </Row>
               <Row className="mb-2">
                  <Col xs="3">
                     <h6>
                        City {!newAddress.city?.length ? helpers.requiredStar() : null}
                     </h6>
                  </Col>
                  <Col xs="9">
                     <DebounceInput
                        value={newAddress.city}
                        onChange={(e) => onChangeNewAddress("city", e.target.value)}
                        debounceTimeout={500}
                     />
                  </Col>
               </Row>
               <Row className="mb-2">
                  <Col xs="3">
                     <h6>
                        State{" "}
                        {!newAddress.state?.length ? helpers.requiredStar() : null}
                     </h6>
                  </Col>
                  <Col xs="9">
                     <DebounceInput
                        value={newAddress.state}
                        onChange={(e) => onChangeNewAddress("state", e.target.value)}
                        debounceTimeout={500}
                     />
                  </Col>
               </Row>
               <Row className="mb-2">
                  <Col xs="3">
                     <h6>
                        Zip Code{" "}
                        {!newAddress.zip?.length ? helpers.requiredStar() : null}
                     </h6>
                  </Col>
                  <Col xs="9">
                     <DebounceInput
                        value={newAddress.zip}
                        onChange={(e) => onChangeNewAddress("zip", e.target.value)}
                        debounceTimeout={500}
                     />
                  </Col>
               </Row>
               <Row className="mb-2">
                  <Col xs="3">
                     <h6>Phone</h6>
                  </Col>
                  <Col xs="9">
                     <DebounceInput
                        value={newAddress.phone}
                        onChange={(e) => onChangeNewAddress("phone", e.target.value)}
                        debounceTimeout={500}
                     />
                  </Col>
               </Row>
               <Row>
                  <Col xs="3">
                     <h6>Fax</h6>
                  </Col>
                  <Col xs="9">
                     <DebounceInput
                        value={newAddress.fax}
                        onChange={(e) => onChangeNewAddress("fax", e.target.value)}
                        debounceTimeout={500}
                     />
                  </Col>
               </Row>
            </ModalBody>
            <ModalFooter>
               <ButtonGroup>
                  <Button
                     children="Yes"
                     className="button-success"
                     onClick={saveNewAddress}
                     disabled={addressSaving}
                  />
                  <Button
                     children="No"
                     className="button-danger"
                     onClick={closeNewAddress}
                     disabled={addressSaving}
                  />
               </ButtonGroup>
            </ModalFooter>
         </Modal>

         <ConfirmModal
            confirmDelete={rowItemDelete}
            modalClassName={modalClassName}
            approveConfirmDelete={() => { onWorkOrderItemDelete(rowItemDelete.id, rowItemDelete.itemNumber); }}
            declineConfirmDelete={() => { setRowItemDelete(null); }}
            disabled={savingDelete}
            warningMessage={
               <>
                  Item number{" "}
                  <strong>{rowItemDelete && rowItemDelete.itemNumber}</strong> is
                  already associated to this existing work order.
                  <br /> Are you sure you want to delete this item from this work order?
               </>
            }
         />
      </>
   );
}

//--------------------------------------------------------------------------------------

export default function MachineWorkOrderProcessing(props) {
   const userCtx = useContext(UserContext);
   const currentUser = userCtx.currentUser;
   const [addressOptions, setAddressOptions] = useState([]);
   const [addStandardParts, setAddStandardParts] = useState(false);
   const [confirmCompleteWO, setConfirmCompleteWO] = useState(null);
   const [confirmDelete, setConfirmDelete] = useState(null);
   const [customer, setCustomer] = useState(null);
   const [deleteItem, setDeleteItem] = useState(null);
   const [downloading, setDownloading] = useState(false);
   const [editItem, setEditItem] = useState(null);
   const [filters, setFilters] = useState([]);
   const [loading, setLoading] = useState(false);
   const [message, setMessage] = useState(null);
   const [pageNumber, setPageNumber] = useState(1);
   const [paginatedList, setPaginatedList] = useState({
      items: [],
      pageNumber: 1,
      totalPages: 1,
   });
   const [partsLoading, setPartsLoading] = useState(false);
   const [priceList, setPriceList] = useState([]);
   const [salespeopleList, setSalespeopleList] = useState(null);
   const [saving, setSaving] = useState(false);
   //const [showAsterisk, setShowAsterisk] = useState(false);
   const [, setShowAsterisk] = useState(false);
   const [standardParts, setStandardParts] = useState([]);
   const modalClassName = userCtx.theme ? `${userCtx.theme}` : "lightTheme";

   useEffect(() => {
      setLoading(false);
      let apiCalls = [];
      apiCalls.push(
         api
            .fetch(`Reference/GetSalespeopleList`)
            .then((response) => {
               return {
                  salespeople: response.data.salespeople,
               };
            })
            .catch(api.catchHandler)
      );
      Promise.all(apiCalls)
         .then((arrayResults) => {
            let aggResults = {};
            _.each(arrayResults, (x) => Object.assign(aggResults, x));
            setSalespeopleList(
               _.chain(aggResults.salespeople)
                  .map((sp) => {
                     return {
                        value: sp.salespersonId,
                        label: sp.salespersonText,
                     };
                  })
                  .sortBy((x) => { return x.label; })
                  .unshift({
                     value: "All",
                     label: "--- All ---",
                  })
                  .value()
            );
         })
         .catch((error) =>
            setMessage({
               flavor: "alert-danger",
               text: "There was an error loading sales people data",
            })
         );
   }, []);

   useEffect(() => {
      refreshData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [filters, pageNumber, customer]);

   useEffect(() => {
      if (!editItem) {
         let itemToEdit = _.find(paginatedList?.items, (x) => {
            return x.editing;
         });
         const isNew = itemToEdit?.machineWorkOrderId < 0;
         if (isNew) {
            itemToEdit.customerId = customer.value;
            itemToEdit.laborRate = constants.MACHINE_WORKORDER_LABOR_RATES;
         }
         if (
            itemToEdit?.machineWorkOrderId !== editItem?.machineWorkOrderId ||
            isNew
         ) {
            if (itemToEdit) {
               itemToEdit.salesPerson = _.find(
                  salespeopleList,
                  (x) =>
                     x.value === itemToEdit.salesPersonId ||
                     x.value === currentUser.salespersonId
               );
               itemToEdit.customer = customer;
            }
            setEditItem(itemToEdit);
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [paginatedList, editItem]);

   useEffect(() => {
      if (customer) {
         fetchStandardPartsPricing();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [customer, addStandardParts]);

   useEffect(() => {
      if (editItem) {
         setShowAsterisk(true);
      }
   }, [editItem]);

   useEffect(() => {
      if ((editItem && editItem.customerId) || customer) {
         fetchCustomerAddresses();
         fetchStandardPartsPricing();
      } else {
         if (!customer) {
            setAddressOptions([]);
            setStandardParts([]);
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [editItem?.customerId, customer]);

   function createNew() {
      if (saving || downloading || partsLoading) return;
      let changes = Object.assign({}, emptyNew, {
         customerId: customer.value,
         salesPerson: null,
      });
      let newList = Object.assign([], changes.workOrderItems);
      if (addStandardParts) {
         _.forEach(standardParts, (x) => {
            let opt = x;
            opt.quantity = 1;
            opt.extendedPrice = x.itemPrice * opt.quantity;
            newList.push(opt);
         });
      }

      if (addressOptions && addressOptions.length) {
         changes.billToAddress =
            _.find(addressOptions, (opt) => opt.value?.toUpperCase() === "MAIN") ||
            _.head(addressOptions);
      }

      if (salespeopleList && salespeopleList.length) {
         let sp =
            _.find(salespeopleList, (op) => op.value === filterHelpers.getValue(filters, "SalespersonId")) ||
            _.find(salespeopleList, (p) => p.value === currentUser.salespersonId) ||
            null;
         changes.salesPersonId = sp?.value;
      }

      let newOrdersList = Object.assign([], paginatedList?.items);
      changes.workOrderItems = newList;
      changes.editing = true;
      newOrdersList.unshift(changes);

      let pagedRes = Object.assign({}, paginatedList);
      pagedRes.items = newOrdersList;
      setPaginatedList(pagedRes);
   }

   function createPayload() {
      let payload = {
         sortField: null,
         sortDirection: null,
         pageNumber: pageNumber,
      };
      _.map(filters, (filter) => (payload[filter.filterName] = filter.value));
      if (customer) {
         payload.CustomerId = customer.value;
      }
      return payload;
   }

   function deleteWorkOrder() {
      if (!saving) {
         setSaving(true);
         api
            .fetch(`SewingTech/DeleteMachineWorkOrder`, {
               MachineWorkOrderId: deleteItem.machineWorkOrderId,
            })
            .then((res) => {
               if (res && res.data && res.data.success) {
                  refreshData();
                  toggleEditRow(null, false);
               }
            })
            .catch(api.catchHandler)
            .finally(() => setSaving(false));
      }
   }

   function downloadWorkOrder(workOrderId, customerId) {
      setDownloading(true);
      const payload = {
         OrderId: _.toString(workOrderId),
         customerId: customerId,
      };
      api
         .postDownloadBinary(
            "Report/MachineWorkOrderReport",
            payload,
            constants.MIME_TYPES.pdf,
            `WorkOrder_${workOrderId}_${dateHelpers.toMDYDateString(new Date())}.pdf`,
            "There was an error downloading this machine work order pdf"
         )
         .then((res) => {
            if (res?.data === false) {
               toast.error(res.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setDownloading(false));
   }

   function fetchCustomerAddresses() {
      let apiCalls = [
         api
            .fetch(`Customer/GetAllAddressInfo/${editItem?.customerId}`)
            .then((response) => {
               return {
                  addresses: response.data?.addresses,
               };
            }),
         api
            .fetch(`Customer/GetPrimaryAddressInfo/${editItem?.customerId}`)
            .then((response) => {
               return {
                  flags: response.data?.flags,
               };
            }),
      ];
      Promise.all(apiCalls)
         .then((arrayResults) => {
            let aggResults = {};
            _.each(arrayResults, (x) => Object.assign(aggResults, x));
            let { addresses, flags } = aggResults;
            if (addresses) {
               addresses = _.map(addresses, (x) => {
                  x.value = x.ddlValue;
                  x.label = x.ddlText;
                  return x;
               });
               setAddressOptions(addresses);
            }
            if (editItem) {
               let changes = Object.assign({}, editItem);
               if (addresses) {
                  changes.btAddressCode = _.find(
                     addresses,
                     (x) =>
                        x.value === changes.btAddressCode ||
                        x.value?.toUpperCase() === "MAIN"
                  );
                  changes.stAddressCode = _.find(
                     addresses,
                     (x) => x.addressCode === changes.stAddressCode
                  );
               }
               if (flags) {
                  changes.flags = flags;
               }
               setEditItem({
                  ...changes,
                  billToAddress: changes.btAddressCode,
                  shipToAddress: changes.stAddressCode,
               });
            }
         })
         .catch(api.catchHandler);
   }

   function fetchStandardPartsPricing() {
      let customerId = customer?.value || editItem.customerId;
      if (!partsLoading) {
         setPartsLoading(true);
         let apiCalls = [
            api
               .post("SewingTech/GetDefaultWorkOrderPricing", {
                  CustomerId: customerId,
               })
               .then((response) => {
                  // A failed load empties the price list: keeping the previous customer's would price this one's parts wrong.
                  if (api.toastFailure(response, PRICING_LOAD_FAILED_MESSAGE, PRICING_LOAD_TOAST_ID)) {
                     return { prices: [] };
                  }
                  return {
                     prices: response.data.list,
                  };
               }),
         ];
         if (addStandardParts) {
            apiCalls.push(
               api
                  .post("SewingTech/GetDefaultMachineWorkOrderParts", {
                     CustomerId: customerId,
                  })
                  .then((response) => {
                     if (api.toastFailure(response, STANDARD_PARTS_LOAD_FAILED_MESSAGE, STANDARD_PARTS_LOAD_TOAST_ID)) {
                        return { defaultParts: [] };
                     }
                     return {
                        defaultParts: response.data.list,
                     };
                  })
            );
         }
         Promise.all(apiCalls)
            .then((arrayResults) => {
               let aggResults = {};
               _.each(arrayResults, (x) => Object.assign(aggResults, x));
               let { prices, defaultParts } = aggResults;
               if (prices && !_.isEqual(prices, priceList)) {
                  setPriceList(prices);
               }
               if (
                  defaultParts
                  // && !_.isEqual(defaultParts, standardParts)
               ) {
                  defaultParts = _.forEach(defaultParts, (prt) => {
                     if (!prt.itemPrice) {
                        prt.itemPrice = prt.listPrice;
                     }
                  });
                  setStandardParts(defaultParts);
               }
            })
            .catch(api.catchHandler)
            .finally(() => setPartsLoading(false));
      }
   }

   function onComplete() {
      if (saving) return;
      let payload = workOrderPayload();
      if (payload) {
         setSaving(true);
         api
            .post("SewingTech/CompleteMachineWorkOrder", payload)
            .then((res) => {
               // A refusal is shown as the server sent it; an HTTP error the interceptor already toasted is left to it.
               if (api.toastFailure(res, COMPLETE_FAILED_MESSAGE)) return;
               refreshData();
               toggleEditRow(null, false);
               setConfirmCompleteWO(null);
            })
            .catch(api.catchHandler)
            .finally(() => setSaving(false));
      }
   }

   function onFilterChange(changedFilter) {
      const filterChanges = filterHelpers.getFilters(filters, changedFilter);
      setFilters(filterChanges);
      setPageNumber(1);
   }

   function onSave(e, printWhenDone = false) {
      let payload = workOrderPayload();
      if (payload) {
         setSaving(true);
         api
            .post("SewingTech/SaveMachineWorkOrder", payload)
            .then((res) => {
               // SaveMachineWorkOrder answers { success, machineWorkOrder }, or { success: false, message } with the
               // reason it was refused - shown as sent. An HTTP error the interceptor already toasted is left to it.
               if (api.toastFailure(res, SAVE_FAILED_MESSAGE)) return;
               const workOrder = res.data.machineWorkOrder;
               // A save that came back with no work order did not happen, as before the server reported outcomes.
               if (!workOrder) {
                  toast.error(SAVE_FAILED_MESSAGE);
                  return;
               }
               refreshData();
               // toggleEditRow(null, false); // Ken massie does not want work order to close after saving
               if (printWhenDone && workOrder.machineWorkOrderId && workOrder.customerId) {
                  downloadWorkOrder(workOrder.machineWorkOrderId, workOrder.customerId);
               }
            })
            .catch(api.catchHandler)
            .finally(() => setSaving(false));
      }
   }

   function onSavePrint(e) {
      onSave(e, true);
   }

   function openDeleteModal(item) {
      setConfirmDelete(true);
      setDeleteItem(item);
   }

   function refreshData() {
      setMessage(null);
      api
         .post("SewingTech/GetMachineWorkOrders", createPayload())
         .then((response) => {
            if (response?.data?.list) {
               let results = response?.data?.list;
               results.totalPages = _.ceil(results.totalCount / 25); // MELISSA TO DO page size
               results.items = _.map(results.items, (x) => {
                  x.invoiceRecipients = _.chain(_.split(x.invoiceRecipients, ","))
                     .reject((e) => {
                        return !e || e === "";
                     })
                     .value();
                  x.inventoryLocation = _.find(
                     constants.MACHINE_WORKORDER_SITES,
                     (opt) => opt.value === x.inventoryLocation
                  );
                  return x;
               });
               setPaginatedList(results);
            }
         })
         .catch(api.catchHandler);
   }

   function saveEditRow(idx) {
      let newList = Object.assign([], paginatedList?.items);
      newList = _.map(newList, (x, index) => {
         x.editing = idx === index ? !x.editing : false;
         return x;
      });
      let changes = Object.assign({}, paginatedList);
      changes.items = newList;
      setPaginatedList(changes);
   }

   function toggleEditRow(idx, resetOverride) {
      let newList = Object.assign([], paginatedList?.items);
      newList = _.map(newList, (x, index) => {
         x.editing = resetOverride || idx === index ? !x.editing : false;
         return x;
      });
      let changes = Object.assign({}, paginatedList);
      changes.items = newList;
      setPaginatedList(changes);
      if (!_.some(newList, (x) => { return x.editing; })) {
         setEditItem(null);
      }
   }

   function workOrderPayload() {
      let warnings = [];
      let payload = {};
      if (editItem.customerId) {
         payload.CustomerId = editItem.customerId;
      } else {
         warnings.push("Customer is required\n");
      }
      if (editItem.salesPerson && editItem.salesPerson.value) {
         payload.SalespersonId = editItem.salesPerson.value;
      } else {
         warnings.push("Salesperson is required\n");
      }
      if (editItem.shipToAddress) {
         payload.STAddressCode = editItem.shipToAddress.addressCode;
         payload.STCompanyName = editItem.shipToAddress.address1;
         payload.STAddress1 = editItem.shipToAddress.address2;
         payload.STAddress2 = editItem.shipToAddress.address3;
         payload.STCity = editItem.shipToAddress.city;
         payload.STState = editItem.shipToAddress.state;
         payload.STZip = editItem.shipToAddress.zip;
         // contact info
         payload.STContactName = editItem.shipToAddress.contact;
         payload.STContactFax = editItem.shipToAddress.fax;
         payload.STContactPhone = editItem.shipToAddress.phone;
      } else {
         warnings.push("Ship to address is required\n");
      }

      payload.MachineWorkOrderId = editItem.machineWorkOrderId > 0 ? editItem.machineWorkOrderId : -1;
      // machine and other basic info
      payload.MachineModel = editItem.machineModel;
      payload.MachineSerial = editItem.machineSerial;
      payload.Notes = editItem.notes;
      payload.InventoryLocation = editItem.inventoryLocation?.value;
      payload.PONumber = editItem.poNumber;
      // invoice email recipients
      if (editItem.invoiceRecipients) {
         payload.InvoiceRecipients = _.join(editItem.invoiceRecipients, ",");
      } else {
         warnings.push("At least one email recipient is required\n");
      }

      payload.FreightQuote = editItem.freightQuote > 0 ? helpers.roundNum(editItem.freightQuote) : 0;
      if (editItem.trackingNumber) {
         payload.TrackingNumber = editItem.trackingNumber;
      }
      // labor
      if (editItem.laborHours) {
         payload.LaborHours = editItem.laborHours;
      } else {
         warnings.push("Labor hours is required\n");
      }
      if (editItem.laborRate) {
         payload.LaborRate = helpers.roundNum(editItem.laborRate);
      } else {
         warnings.push("Labor rate is required\n");
      }

      //shipping cost
      payload.ShippingCost = editItem.shippingCost > 0 ? editItem.shippingCost : 0;

      if (!editItem.billToAddress || !editItem.billToAddress.address1) {
         warnings.push("Bill to address is required\n");
      } else {
         payload.BTAddressCode = editItem.billToAddress.addressCode;
         payload.BTCompanyName = editItem.billToAddress.address1;
         payload.BTAddress1 = editItem.billToAddress.address2;
         payload.BTAddress2 = editItem.billToAddress.address3;
         payload.BTCity = editItem.billToAddress.city;
         payload.BTState = editItem.billToAddress.state;
         payload.BTZip = editItem.billToAddress.zip;
      }
      let itemList = [];
      if (editItem.workOrderItems?.length > 0) {
         if (_.some(editItem.workOrderItems, (i) => { return !i.itemNumber; })) {
            warnings.push("Every line item must have an item number selected\n");
         }
         // else if (_.some(editItem.workOrderItems, (i) => { return !i.itemPrice || i.itemPrice < 1; })) {
         //   warnings.push("Every line item must have a price\n");
         // }
         else if (_.some(editItem.workOrderItems, (i) => { return !i.quantity || i.quantity < 1; })) {
            warnings.push("Every line item must have a quantity\n");
         } else {
            _.forEach(editItem.workOrderItems, (i) => {
               let item = {
                  ItemDescription: i.itemDescription,
                  ItemNumber: i.itemNumber,
                  ItemPrice: i.itemPrice || parseFloat(i.listPrice),
                  MachineWorkOrderId: payload.MachineWorkOrderId,
                  MachineWorkOrderDetailId: i.machineWorkOrderDetailId > 0 ? i.machineWorkOrderDetailId : -1,
                  Quantity: i.quantity,
               };
               itemList.push(item);
            });
         }
      }
      if (warnings.length) {
         toast.error(warnings.join(''));
         return;
      } else {
         // finally shaping into VM before returning
         return {
            MachineWorkOrder: payload,
            WorkOrderItems: itemList,
         };
      }
   }


   return (
      <DesktopContainer screenName="Machine Work Orders">
         <FilterableScreen
            filters={
               <div className="filter-header mb-3">
                  <Row className="justify-content-center pe-5">
                     <Col xs={2}>
                        <h5 children="Customer:" className="float-end text-white" />
                     </Col>
                     <Col xs={8}>
                        <CustomerTypeahead
                           value={customer}
                           onChange={(e) => {
                              toggleEditRow(null, false);
                              setCustomer(e);
                           }}
                        />
                     </Col>
                  </Row>
                  <Row className="justify-content-center pe-5 my-1">
                     <Col xs={2}>
                        <h5 children="Salesperson:" className="float-end text-white" />
                     </Col>
                     <Col xs={8}>
                        <FilterSelect
                           filterName="SalespersonId"
                           displayName="Salesperson"
                           hideDisplayName
                           value={filterHelpers.getValue(filters, "SalespersonId")}
                           onChangeCallback={(e) => onFilterChange(e)}
                           options={salespeopleList}
                           isClearable
                        />
                     </Col>
                  </Row>
                  <Row className="justify-content-center pe-5 my-2">
                     <Col xs={3}>
                        <h5 children="Start Ship Date:" className="float-end text-white" />
                     </Col>
                     <Col xs={2}>
                        <FilterDateTime
                           filterName="StartDate"
                           value={filterHelpers.getValue(filters, "StartDate")}
                           onChangeCallback={(e) => onFilterChange(e)}
                        />
                     </Col>
                     <Col xs={2}>
                        <h5 children="End Ship Date:" className="float-end text-white" />
                     </Col>
                     <Col xs={2}>
                        <FilterDateTime
                           filterName="EndDate"
                           value={filterHelpers.getValue(filters, "EndDate")}
                           onChangeCallback={(e) => onFilterChange(e)}
                        />
                     </Col>
                  </Row>
                  <Row className="justify-content-center pe-5 my-2">
                     <Col xs={3}>
                        <h5 children="Show Completed:" className="float-end text-white" />
                     </Col>
                     <Col xs={1} className="pe-0">
                        <FilterSwitch
                           filterName="ShowCompleted"
                           value={filterHelpers.getValue(filters, "ShowCompleted")}
                           onChangeCallback={(e) => onFilterChange(e)}
                        />
                     </Col>
                     <Col xs={5} className="ps-0">
                        <Row className="m-0 px-0">
                           <Col xs={8}>
                              <Button
                                 className="me-2 float-end shredButton"
                                 title={"Create New Estimate"}
                                 onClick={() => {
                                    !customer
                                       ? toast.warning("Please select customer first")
                                       : createNew();
                                 }}
                                 disabled={
                                    saving || downloading || partsLoading
                                    // || (customer &&
                                    //     addStandardParts &&
                                    //     standardParts.length < 1)
                                 }
                              >
                                 <FontAwesomeIcon className="whiteIcon me-2" icon="plus" />{" "}
                                 Create New Estimate
                              </Button>
                           </Col>
                           <Col xs={4} className="fontSize85 text-white text-end">
                              <div className="float-start">
                                 <Input
                                    type="checkbox"
                                    checked={addStandardParts}
                                    onChange={() => { setAddStandardParts(!addStandardParts); }}
                                    className="me-3 successCheckBox"
                                 />
                                 <i>Add Standard Parts</i>
                              </div>
                           </Col>
                        </Row>
                        <Row className="mt-2 fontSize85 text-white text-end">
                           <Col xs="12"></Col>
                        </Row>
                     </Col>
                  </Row>
               </div>
            }
            pager={
               editItem ? null : (
                  <div className="float-start">
                     <Pager
                        pageNumber={paginatedList?.pageNumber ? paginatedList.pageNumber : 0}
                        totalPages={paginatedList?.totalPages ? paginatedList.totalPages : 0}
                        callBack={(newPageNumber) => setPageNumber(newPageNumber)}
                     />
                  </div>
               )
            }
         >
            <ServiceThreadSpinner on={loading}>
               {editItem ? (
                  <AddEditItem
                     editItem={editItem}
                     setEditItem={setEditItem}
                     modalClassName={modalClassName}
                     toggleEditRow={toggleEditRow}
                     addressOptions={addressOptions}
                     fetchCustomerAddresses={fetchCustomerAddresses}
                     salespeopleList={salespeopleList}
                     saving={saving}
                     priceList={priceList}
                     onSave={onSave}
                     onSavePrint={onSavePrint}
                     onComplete={onComplete}
                     downloadWorkOrder={downloadWorkOrder}
                     setConfirmCompleteWO={setConfirmCompleteWO}
                  />
               ) : (
                  <Card>
                     <CardBody>
                        <Row className="p-0 m-0">
                           <Col xs="12">
                              {message ? (
                                 <Alert2 className={message.flavor}>{message.text}</Alert2>
                              ) : null}
                              <Table striped hover size="sm" responsive={false} id="userTable">
                                 <thead className="fontSize85">
                                    <tr>
                                       <th width="4%">WO #</th>
                                       <th>Cust #</th>
                                       <th width="15%">Company</th>
                                       <th width="9%">Created</th>
                                       <th>PO #</th>
                                       <th width="15%">Model #</th>
                                       <th>Serial #</th>
                                       <th>Tracking #</th>
                                       <th>Status</th>
                                       <th width="9%">Completed</th>
                                       <th>Est.Total</th>
                                       <th />
                                    </tr>
                                 </thead>
                                 <tbody>
                                    {paginatedList &&
                                       paginatedList.items &&
                                       paginatedList.items.length > 0
                                       ? _.map(paginatedList.items, (t, index) => {
                                          return t.machineWorkOrderId < 1 ? null : (
                                             <tr key={`workOrder-${t.machineWorkOrderId}`} className="fontSize85">
                                                <td>{t.machineWorkOrderId}</td>
                                                <td>{t.customerId}</td>
                                                <td>{t.btCompanyName || t.stCompanyName}</td>
                                                <td>{dateHelpers.toMDYDateString(t.createdDate)}</td>
                                                <td>{t.poNumber}</td>
                                                <td>{t.machineModel}</td>
                                                <td>{t.machineSerial}</td>
                                                <td>{t.trackingNumber}</td>
                                                <td>{t.completedStatusText}</td>
                                                <td>{t.completedStatusText === "Completed" && dateHelpers.toMDYDateString(t.completedDate)}</td>
                                                <td>{helpers.formatCurrency(t.orderTotalFull)}</td>
                                                <td>
                                                   <ButtonGroup className="btn-group-sm btn-sm float-end">
                                                      <Button
                                                         className={`icon-button me-1 btn-sm`}
                                                         color={t.editing ? "success" : "info"}
                                                         title={t.editing ? "Save changes" : "Edit work order"}
                                                         onClick={() => { t.editing ? saveEditRow(index) : toggleEditRow(index); }}
                                                         disabled={saving || downloading}
                                                      >
                                                         <FontAwesomeIcon className="whiteIcon" icon={t.editing ? "check" : "edit"} />
                                                      </Button>
                                                      <Button
                                                         color={t.editing ? "secondary" : "danger"}
                                                         className="icon-button btn-sm me-1"
                                                         title={t.editing ? "Cancel" : "Delete"}
                                                         onClick={() => {
                                                            t.editing
                                                               ? toggleEditRow(index)
                                                               : openDeleteModal(t);
                                                         }}
                                                         disabled={saving || downloading}
                                                      >
                                                         <FontAwesomeIcon className="whiteIcon" icon={t.editing ? "times" : "trash"} />
                                                      </Button>
                                                      <Button
                                                         className="shredButton icon-button btn-sm"
                                                         title={"Print"}
                                                         onClick={() => downloadWorkOrder(t.machineWorkOrderId, t.customerId)}
                                                         disabled={saving || downloading}
                                                      >
                                                         <FontAwesomeIcon className="whiteIcon" icon={"print"} />
                                                      </Button>
                                                   </ButtonGroup>
                                                </td>
                                             </tr>
                                          );
                                       })
                                       : null}
                                 </tbody>
                              </Table>
                           </Col>
                        </Row>
                     </CardBody>
                  </Card>
               )}

               <ConfirmModal
                  confirmDelete={confirmDelete}
                  modalClassName={modalClassName}
                  approveConfirmDelete={() => {
                     setConfirmDelete(null);
                     deleteWorkOrder();
                  }}
                  declineConfirmDelete={() => {
                     setConfirmDelete(null);
                     setDeleteItem(null);
                  }}
                  warningMessage={
                     <>
                        Are you sure you want to delete this work order{" "}
                        <strong>#{deleteItem && deleteItem.machineWorkOrderId}</strong>?
                     </>
                  }
               />
               <ConfirmModal
                  confirmDelete={confirmCompleteWO}
                  header={`Confirm Invoice`}
                  modalClassName={modalClassName}
                  approveConfirmDelete={onComplete}
                  declineConfirmDelete={() => { setConfirmCompleteWO(null); }}
                  warningMessage={
                     <>
                        Are you sure you want to transfer this work order to an invoice?
                        <br />
                        After transferring you will not be able to edit this work order.
                     </>
                  }
                  disabled={saving}
               />
            </ServiceThreadSpinner>
         </FilterableScreen>
      </DesktopContainer>
   );
}
